import { useAtomValue } from "@effect/atom-react";
import { ArrowLeftIcon, ChatSquare2Icon, SettingsIcon } from "lucide-react";
import { memo, useCallback, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";

import { useI18n } from "~/i18n";
import { useMorrowClerkAuth } from "../clerk/morrowClerkAuth";
import { useEnvironmentIdentificationMode } from "../../hooks/useSettings";
import { shortcutLabelForCommand } from "../../keybindings";
import { readLastChatNavigation } from "../../lastChatRoute";
import { cn } from "../../lib/utils";
import { primaryServerKeybindingsAtom } from "../../state/server";
import {
  resolveEnvironmentIdentificationPillLabel,
  resolveSidebarStageFocusRingOffsetClass,
  useEnvironmentStageLabel,
  useSidebarStageBackdropVariant,
} from "../SidebarStageBackdrop";
import { Badge } from "../ui/badge";
import {
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "../ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { SidebarProviderUpdatePill } from "./SidebarProviderUpdatePill";
import { SidebarUpdateArchitectureWarning, SidebarUpdatePill } from "./SidebarUpdatePill";

export const SidebarChromeHeader = memo(function SidebarChromeHeader({
  isElectron,
}: {
  isElectron: boolean;
}) {
  const stageLabel = useEnvironmentStageLabel();
  const environmentIdentificationMode = useEnvironmentIdentificationMode();
  const pillLabel =
    environmentIdentificationMode === "pill"
      ? resolveEnvironmentIdentificationPillLabel(stageLabel)
      : null;

  return (
    <SidebarHeader
      className={cn(
        "@container/sidebar-header relative shrink-0 gap-0 p-0",
        isElectron && "drag-region",
      )}
    >
      <div aria-hidden="true" className="h-[var(--workspace-traffic-lights-height)] shrink-0" />
      <div className="relative flex h-[var(--workspace-brand-row-height)] shrink-0 flex-row items-center gap-1.5 px-3 py-0 max-md:justify-end md:pl-0 md:pr-2">
        <SidebarBrand />
        {pillLabel ? (
          <Badge
            className="relative z-10 ml-1 rounded-full px-1.5 text-muted-foreground max-md:hidden"
            data-environment-identification="pill"
            size="sm"
            variant="secondary"
          >
            {pillLabel}
          </Badge>
        ) : null}
        <SidebarHeaderToggle />
      </div>
    </SidebarHeader>
  );
});

function SidebarHeaderToggle() {
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const environmentIdentificationMode = useEnvironmentIdentificationMode();
  const stageBackdropVariant = useSidebarStageBackdropVariant(
    environmentIdentificationMode === "artwork",
  );
  const shortcutLabel = shortcutLabelForCommand(keybindings, "sidebar.toggle");

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <SidebarTrigger
            aria-label="Toggle main sidebar"
            className={cn(
              "relative z-10 shrink-0 md:ml-auto",
              stageBackdropVariant &&
                "focus-visible:ring-white/90 [&_svg]:stroke-white/90! [&_svg]:opacity-100! [&_svg]:hover:stroke-white! [:hover,[data-pressed]]:bg-white/15",
              stageBackdropVariant && resolveSidebarStageFocusRingOffsetClass(stageBackdropVariant),
            )}
          />
        }
      />
      <TooltipPopup side="bottom">
        Toggle main sidebar{shortcutLabel ? ` (${shortcutLabel})` : ""}
      </TooltipPopup>
    </Tooltip>
  );
}

function SidebarBrand() {
  const { t } = useI18n();
  return (
    <Link
      aria-label={t("sidebar.goToThreads")}
      className={cn(
        // Mobile: mascot only in the sidebar sheet (no titlebar offset). Desktop:
        // mascot + wordmark aligned with the collapsed-sidebar titlebar inset.
        "relative z-10 flex h-7 w-fit min-w-0 shrink-0 items-center gap-1.5 overflow-hidden rounded-md text-foreground outline-hidden ring-ring focus-visible:ring-2 md:ml-[var(--workspace-brand-left)]",
      )}
      to="/"
    >
      <img
        alt=""
        aria-hidden="true"
        className="size-6 shrink-0 object-contain"
        src="/brand/morrow-mascot.png"
      />
      <img
        alt=""
        aria-hidden="true"
        className="hidden h-3.5 w-auto shrink-0 object-contain object-left md:block dark:md:hidden"
        src="/brand/morrow-wordmark-ink.png"
      />
      <img
        alt=""
        aria-hidden="true"
        className="hidden h-3.5 w-auto shrink-0 object-contain object-left md:dark:block"
        src="/brand/morrow-wordmark.png"
      />
    </Link>
  );
}

export const SidebarChromeFooter = memo(function SidebarChromeFooter({
  account,
}: {
  account?: ReactNode;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const { isLoaded: clerkLoaded, isSignedIn, openSignIn } = useMorrowClerkAuth();
  // Settings / Usage / Memory are account features — a signed-out visitor gets
  // the Clerk sign-in modal instead of an empty page. Returns true if it gated.
  const gateSignedOut = useCallback(() => {
    if (clerkLoaded && !isSignedIn) {
      openSignIn();
      return true;
    }
    return false;
  }, [clerkLoaded, isSignedIn, openSignIn]);
  const currentFooterPage = useLocation({
    select: (location) =>
      location.pathname === "/usage"
        ? "usage"
        : location.pathname === "/pricing"
          ? "pricing"
          : location.pathname === "/team"
            ? "team"
            : location.pathname === "/agents"
              ? "agents"
              : location.pathname === "/pull-requests"
                ? "pull-requests"
                : null,
  });
  const closeMobileSidebar = useCallback(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [isMobile, setOpenMobile]);
  const handleSettingsClick = useCallback(() => {
    if (gateSignedOut()) return;
    closeMobileSidebar();
    void navigate({ to: "/settings" });
  }, [gateSignedOut, closeMobileSidebar, navigate]);

  const handleUsageClick = useCallback(() => {
    if (gateSignedOut()) return;
    if (isMobile) {
      setOpenMobile(false);
    }
    void navigate({ to: "/usage" });
  }, [gateSignedOut, isMobile, navigate, setOpenMobile]);

  const handleBackClick = useCallback(() => {
    closeMobileSidebar();
    // `/` always mints a fresh empty draft. Return to the thread the user
    // left — Team / Agents / Usage are side pages, not a new chat.
    void navigate(readLastChatNavigation());
  }, [closeMobileSidebar, navigate]);

  return (
    <SidebarFooter className="p-[var(--sidebar-content-inset)]">
      <SidebarProviderUpdatePill />
      <SidebarUpdateArchitectureWarning />
      <SidebarMenu className="flex-row items-center">
        {currentFooterPage ? (
          <SidebarMenuItem className="min-w-0 flex-1">
            <SidebarMenuButton onClick={handleBackClick}>
              <ArrowLeftIcon />
              <span>{t("common.back")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        ) : (
          <>
            {account}
            <SidebarMenuItem className="shrink-0">
              <Tooltip>
                <TooltipTrigger
                  render={
                    <SidebarMenuButton
                      aria-label={t("sidebar.settings")}
                      onClick={handleSettingsClick}
                      size="icon"
                    >
                      <SettingsIcon />
                    </SidebarMenuButton>
                  }
                />
                <TooltipPopup side="top">{t("sidebar.settings")}</TooltipPopup>
              </Tooltip>
            </SidebarMenuItem>
            <SidebarMenuItem className="shrink-0">
              <Tooltip>
                <TooltipTrigger
                  render={
                    <SidebarMenuButton
                      aria-label={t("sidebar.usage")}
                      onClick={handleUsageClick}
                      size="icon"
                    >
                      <ChatSquare2Icon />
                    </SidebarMenuButton>
                  }
                />
                <TooltipPopup side="top">{t("sidebar.usage")}</TooltipPopup>
              </Tooltip>
            </SidebarMenuItem>
          </>
        )}
        <SidebarUpdatePill />
      </SidebarMenu>
    </SidebarFooter>
  );
});
